import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Tools|Exams Under Review|Face Recognition|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamSecurity,
	portal: 'SP',
};

export type SPFaceRecognitionReviewLocNames =
	| 'SPFaceRecognitionReviewLabelName'
	| 'SPFaceRecognitionReviewLabelCmcId'
	| 'SPFaceRecognitionReviewLabelGrade'
	| 'SPFaceRecognitionReviewLabelRegistrationNumber'
	| 'SPFaceRecognitionReviewLabelDate'
	| 'SPFaceRecognitionReviewLabelExternalId'
	| 'SPFaceRecognitionReviewNotificationSuccess'
	| 'SPFaceRecognitionReviewContentOriginalDetailsSection'
	| 'SPFaceRecognitionReviewContentMatchedDetailsSection'
	| 'SPFaceRecognitionReviewPageTitle'
	| 'SPFaceRecognitionReviewButtonBack'
	| 'SPFaceRecognitionReviewButtonNext'
	| 'SPFaceRecognitionReviewImageNotFound'
	| 'SPFaceRecognitionReviewButtonCloseImage'
	| 'SPFaceRecognitionReviewButtonViewFullImage'
	| 'SPFaceRecognitionReviewButtonMergeOriginalAsWinner'
	| 'SPFaceRecognitionReviewButtonMergeMatchedAsWinner';

export const spFaceRecognitionReviewLocs: LocDefs<SPFaceRecognitionReviewLocNames> = {
	SPFaceRecognitionReviewLabelName: {
		defaultValue: 'Candidate Name',
		description: 'Label for name of the main and the matched candidate',
		displayPath: `${pathStart}Candidate Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPFaceRecognitionReviewLabelCmcId: {
		defaultValue: 'Cmc Id',
		description: 'Label for Cmc Id of the main and the matched candidate',
		displayPath: `${pathStart}Cmc Id`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPFaceRecognitionReviewLabelGrade: {
		defaultValue: 'Grade',
		description: 'Label for grade of the main and the matched candidate',
		displayPath: `${pathStart}Grade`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPFaceRecognitionReviewLabelRegistrationNumber: {
		defaultValue: 'Registration Number',
		description: 'Label for registration number of the main and the matched candidate',
		displayPath: `${pathStart}Registration Number`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPFaceRecognitionReviewLabelDate: {
		defaultValue: 'Date Taken',
		description: 'Label for date of the main and the matched candidate',
		displayPath: `${pathStart}Date`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPFaceRecognitionReviewLabelExternalId: {
		defaultValue: 'Client Id',
		description: 'Label for external id of the main and the matched candidate',
		displayPath: `${pathStart}External Id`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPFaceRecognitionReviewNotificationSuccess: {
		defaultValue: 'Exam reviewed successfully',
		description: 'Success message for exam review',
		displayPath: `${pathStart}Success Notification`,
		displayType: 'Notification',
		...sharedProperties,
	},
	SPFaceRecognitionReviewContentOriginalDetailsSection: {
		defaultValue: 'Candidate Details',
		description: 'Content for candidate details section with the exam image',
		displayPath: `${pathStart}Candidate Details`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPFaceRecognitionReviewContentMatchedDetailsSection: {
		defaultValue: 'Match Details',
		description: 'Content for matched candidate details with the images',
		displayPath: `${pathStart}Match Details`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPFaceRecognitionReviewPageTitle: {
		defaultValue: 'Exams under review',
		description: 'Title for the face recognition review page',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPFaceRecognitionReviewButtonBack: {
		defaultValue: 'Back',
		description: 'Button for going back to the previous exam',
		displayPath: `${pathStart}Button Back`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPFaceRecognitionReviewButtonNext: {
		defaultValue: 'Next',
		description: 'Button for going to the next exam',
		displayPath: `${pathStart}Button Next`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPFaceRecognitionReviewImageNotFound: {
		defaultValue: 'Image Not Found',
		description: 'Content for image not found',
		displayPath: `${pathStart}Image Not Found`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPFaceRecognitionReviewButtonCloseImage: {
		defaultValue: 'Close',
		description: 'Button for closing the image',
		displayPath: `${pathStart}Button Close Image`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPFaceRecognitionReviewButtonViewFullImage: {
		defaultValue: 'View Full Image',
		description: 'Button for viewing the full image',
		displayPath: `${pathStart}Button View Full Image`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPFaceRecognitionReviewButtonMergeOriginalAsWinner: {
		defaultValue: 'Merge as Winner',
		description: 'Button for merging the original candidate as winner',
		displayPath: `${pathStart}Button Merge Original Winner`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPFaceRecognitionReviewButtonMergeMatchedAsWinner: {
		defaultValue: 'Merge as Winner',
		description: 'Button for merging the matched candidate as winner',
		displayPath: `${pathStart}Button Merge Matched Winner`,
		displayType: 'Button',
		...sharedProperties,
	},
};

export type SPFaceRecognitionReviewLocType = LocMap<SPFaceRecognitionReviewLocNames>;
